/**
 * Word difficulty scoring and per-level word selection.
 *
 * Pure logic: no Phaser imports. The word lists themselves are injected, so tests
 * use stubs and Prompt 3 swaps in the real grade3..grade8 banks.
 */
import type { GradeBand, WordKind } from '../types';
import type { Rng } from './rng';

/** Letters that are awkward for small hands to reach (CLAUDE.md scoring formula). */
export const DIFFICULT_LETTERS = 'qzxjkvbpyw';

export const GRADE_BANDS: readonly GradeBand[] = [3, 4, 5, 6, 7, 8];

export interface ScoredWord {
  readonly word: string;
  readonly score: number;
}

export type WordLists = Partial<Record<GradeBand, readonly string[]>>;

/** Each band sorted ascending by difficulty, so a percentile window is a slice. */
export interface WordBank {
  readonly byBand: ReadonlyMap<GradeBand, readonly ScoredWord[]>;
}

/**
 * score = length
 *       + 0.5 * (letters drawn from "qzxjkvbpyw")
 *       + 0.5 * (adjacent double letters)
 *       + 1   * (contains a capital)
 */
export function scoreWord(word: string): number {
  const lower = word.toLowerCase();
  let awkward = 0;
  let doubles = 0;
  for (let i = 0; i < lower.length; i += 1) {
    const ch = lower[i] as string;
    if (DIFFICULT_LETTERS.includes(ch)) awkward += 1;
    if (i > 0 && ch === lower[i - 1]) doubles += 1;
  }
  const hasCapital = /[A-Z]/.test(word) ? 1 : 0;
  return word.length + 0.5 * awkward + 0.5 * doubles + hasCapital;
}

function compareScored(a: ScoredWord, b: ScoredWord): number {
  // Alphabetical tie-break keeps the sort stable across engines.
  return a.score - b.score || a.word.localeCompare(b.word, 'en');
}

export function buildWordBank(lists: WordLists): WordBank {
  const byBand = new Map<GradeBand, readonly ScoredWord[]>();
  for (const band of GRADE_BANDS) {
    const words = lists[band];
    if (words === undefined) continue;
    const unique = Array.from(new Set(words));
    const scored = unique.map((word) => ({ word, score: scoreWord(word) }));
    scored.sort(compareScored);
    byBand.set(band, scored);
  }
  return { byBand };
}

/** Inclusive slice of a band by percentile position. */
export function percentileSlice(
  list: readonly ScoredWord[],
  lo: number,
  hi: number,
): readonly ScoredWord[] {
  if (list.length === 0) return [];
  const last = list.length - 1;
  const a = clampIndex(Math.round(Math.min(lo, hi) * last), last);
  const b = clampIndex(Math.round(Math.max(lo, hi) * last), last);
  return list.slice(a, b + 1);
}

function clampIndex(value: number, last: number): number {
  if (value < 0) return 0;
  if (value > last) return last;
  return value;
}

export interface PickedWord {
  readonly word: string;
  readonly score: number;
  readonly kind: WordKind;
  /** Upper half of its difficulty class: spring +3 instead of +2, jetpack +6 instead of +5. */
  readonly topHalf: boolean;
}

export interface WordPickerOptions {
  /** CLAUDE.md: lowercase only through level 45. */
  readonly allowCapitals: boolean;
}

interface Window {
  readonly lo: number;
  readonly hi: number;
}

/**
 * One picker per level. Holds the level RNG and the words already used, so a word
 * never repeats inside a level.
 */
export class WordPicker {
  private readonly used = new Set<string>();

  constructor(
    private readonly bank: WordBank,
    private readonly band: GradeBand,
    private readonly progress: number,
    private readonly rng: Rng,
    private readonly options: WordPickerOptions,
  ) {}

  /** Count words as already used, so they are never drawn (a level in play). */
  markUsed(words: Iterable<string>): void {
    for (const word of words) if (word !== '') this.used.add(word);
  }

  /** Words handed out so far, in pick order. */
  get usedCount(): number {
    return this.used.size;
  }

  pick(kind: WordKind): PickedWord {
    const { source, window } = this.sourceFor(kind);
    const chosen = this.drawFrom(source, window);
    this.used.add(chosen.word);
    return {
      word: chosen.word,
      score: chosen.score,
      kind,
      topHalf: kind === 'normal' ? false : this.isTopHalf(chosen, source, window),
    };
  }

  /**
   * Top half of its difficulty class, by position in the window rather than by raw
   * score: scores tie heavily (they are dominated by word length), and a score
   * comparison would call almost everything "top half".
   */
  private isTopHalf(chosen: ScoredWord, source: readonly ScoredWord[], window: Window): boolean {
    const slice = percentileSlice(source, window.lo, window.hi);
    const position = slice.findIndex((w) => w.word === chosen.word);
    if (position === -1) {
      // Drawn from a widened window; fall back to comparing against the midpoint.
      return chosen.score >= this.midpointScore(source, window);
    }
    return position >= Math.floor(slice.length / 2);
  }

  private sourceFor(kind: WordKind): { source: readonly ScoredWord[]; window: Window } {
    if (kind === 'hard') return this.hardDraw();
    const window: Window =
      kind === 'moderate'
        ? { lo: 0.7, hi: 0.9 }
        : { lo: 0.2 * this.progress, hi: 0.45 + 0.25 * this.progress };
    return { source: this.bandWords(this.band), window };
  }

  /** Jetpack words: 90th+ of this band, or the upper-middle of the next band up. */
  private hardDraw(): { source: readonly ScoredWord[]; window: Window } {
    const next = nextBandOf(this.band);
    if (next !== null) {
      const harder = this.bandWords(next);
      // The next band's top decile would be brutal, so take its upper-middle.
      if (harder.length > 0 && this.rng.chance(0.5)) {
        return { source: harder, window: { lo: 0.55, hi: 0.9 } };
      }
    }
    return { source: this.bandWords(this.band), window: { lo: 0.9, hi: 1 } };
  }

  private bandWords(band: GradeBand): readonly ScoredWord[] {
    const list = this.bank.byBand.get(band) ?? [];
    return this.options.allowCapitals ? list : list.filter((w) => !/[A-Z]/.test(w.word));
  }

  /**
   * Draw inside the window, widening outward if everything there is used, and
   * finally taking any unused word in the source.
   */
  private drawFrom(source: readonly ScoredWord[], window: Window): ScoredWord {
    if (source.length === 0) throw new RangeError('WordPicker: no words for this grade band');

    for (let step = 0; step <= 6; step += 1) {
      const lo = Math.max(0, window.lo - step * 0.1);
      const hi = Math.min(1, window.hi + step * 0.1);
      const available = percentileSlice(source, lo, hi).filter((w) => !this.used.has(w.word));
      if (available.length > 0) return this.rng.pick(available);
    }

    const anyUnused = source.filter((w) => !this.used.has(w.word));
    if (anyUnused.length > 0) return this.rng.pick(anyUnused);

    // Band exhausted: repeat is better than crashing mid-level.
    return this.rng.pick(source);
  }

  /** Midpoint score of the window, for the top-half test. */
  private midpointScore(source: readonly ScoredWord[], window: Window): number {
    const slice = percentileSlice(source, window.lo, window.hi);
    if (slice.length === 0) return Number.POSITIVE_INFINITY;
    const mid = slice[Math.floor((slice.length - 1) / 2)];
    return mid === undefined ? Number.POSITIVE_INFINITY : mid.score;
  }
}

/** The next grade band up, or null at grade 8. */
export function nextBandOf(band: GradeBand): GradeBand | null {
  const index = GRADE_BANDS.indexOf(band);
  const next = index === -1 ? undefined : GRADE_BANDS[index + 1];
  return next ?? null;
}

/** Progress (0..1) of a level inside its grade band, for the sliding normal window. */
export function bandProgress(level: number, bandStart: number, bandEnd: number): number {
  if (bandEnd <= bandStart) return 0;
  const t = (level - bandStart) / (bandEnd - bandStart);
  return Math.min(1, Math.max(0, t));
}
